import { motion } from 'framer-motion';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="relative bg-primary text-white overflow-hidden">
      {/* Background Text Effect - Hidden on mobile */}
      <div className="hidden md:block absolute inset-0 overflow-hidden pointer-events-none select-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full">
          <h2 className="text-[12vw] font-black text-white/5 whitespace-nowrap leading-none text-center uppercase tracking-tight">
            PRECISION CUTTING
          </h2>
          <h2 className="text-[12vw] font-black text-white/5 whitespace-nowrap leading-none text-center uppercase tracking-tight -mt-8">
            EXCELLENCE
          </h2>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero CTA Section */}
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-16">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
            {/* Main Heading */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl"
            >
              <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.9] tracking-tight text-white">
                Precision<br />
                Laser Cutting<br />
                <span className="text-white/80">Excellence!</span>
              </h2>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Link to="/contact">
                <motion.div
                  className="w-36 h-36 sm:w-44 sm:h-44 rounded-full bg-white flex flex-col items-center justify-center text-primary font-bold text-center cursor-pointer group"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="text-sm sm:text-base uppercase tracking-wider">Get a</span>
                  <span className="text-lg sm:text-xl uppercase tracking-wider">Quote!</span>
                  <ArrowRight className="w-5 h-5 mt-2 group-hover:translate-x-1 transition-transform" />
                </motion.div>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Footer Links Section */}
        <div className="border-t border-white/20">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              {/* Address */}
              <div>
                <h4 className="text-sm font-bold uppercase tracking-wider mb-4 text-white">
                  Address
                </h4>
                <div className="space-y-1 text-white/80 text-sm">
                  <p>184/2, Infront of the IEC Bus Stop,</p>
                  <p>Barathiyar Road, ManiyakaramPalayam,</p>
                  <p>Ganapathy, Coimbatore-641006</p>
                  <p className="mt-6 font-semibold text-white/90">GSTIN: 33ESZPR8850J1ZM</p>
                </div>
              </div>

              {/* Links */}
              <div>
                <h4 className="text-sm font-bold uppercase tracking-wider mb-4 text-white">
                  Links
                </h4>
                <nav className="space-y-2">
                  <Link to="/" className="block text-white/80 hover:text-white transition-colors text-sm">
                    Home
                  </Link>
                  <Link to="/services" className="block text-white/80 hover:text-white transition-colors text-sm">
                    Services
                  </Link>
                  <Link to="/about" className="block text-white/80 hover:text-white transition-colors text-sm">
                    About
                  </Link>
                  <Link to="/contact" className="block text-white/80 hover:text-white transition-colors text-sm">
                    Contact
                  </Link>
                </nav>
              </div>

              {/* Services */}
              <div>
                <h4 className="text-sm font-bold uppercase tracking-wider mb-4 text-white">
                  Services
                </h4>
                <nav className="space-y-2">
                  <Link to="/services/laser-cutting" className="block text-white/80 hover:text-white transition-colors text-sm">
                    Laser Cutting
                  </Link>
                  <Link to="/services/cnc-bending" className="block text-white/80 hover:text-white transition-colors text-sm">
                    CNC Bending
                  </Link>
                  <Link to="/services/welding" className="block text-white/80 hover:text-white transition-colors text-sm">
                    Welding
                  </Link>
                  <Link to="/services/surface-coating" className="block text-white/80 hover:text-white transition-colors text-sm">
                    Surface Coating
                  </Link>
                </nav>
              </div>

              {/* Contact */}
              <div>
                <h4 className="text-sm font-bold uppercase tracking-wider mb-4 text-white">
                  Contact
                </h4>
                <div className="space-y-2 text-white/80 text-sm">
                  <a href="mailto:alphalaserinfo@gmail.com" className="block hover:text-white transition-colors">
                    alphalaserinfo@gmail.com
                  </a>
                  <a href="tel:+919363638811" className="block hover:text-white transition-colors">
                    +91 93 63 63 88 11
                  </a>
                  <a href="tel:+919363638822" className="block hover:text-white transition-colors">
                    +91 93 63 63 88 22
                  </a>
                </div>
                <div className="mt-6">
                  <h5 className="text-xs font-bold uppercase tracking-wider mb-3 text-white">
                    Follow Us
                  </h5>
                  <div className="flex items-center gap-3">
                    <a
                      href="https://www.facebook.com/profile.php?id=61587464441215"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#1877F2] hover:scale-110 transition-transform"
                    >
                      <Facebook className="w-6 h-6" />
                    </a>
                    <a
                      href="https://www.instagram.com/alphalasertechnologies/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#E4405F] hover:scale-110 transition-transform"
                    >
                      <Instagram className="w-6 h-6" />
                    </a>
                    <a
                      href="https://www.linkedin.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#0A66C2] hover:scale-110 transition-transform"
                    >
                      <Linkedin className="w-6 h-6" />
                    </a>
                    <a
                      href="https://www.youtube.com/@alphalasertechnologies"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="YouTube"
                      className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#FF0000] hover:scale-110 transition-transform"
                    >
                      <Youtube className="w-6 h-6" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-white/60 text-sm">
                Developed by <span className="text-white font-medium">NameCraftX</span>
              </p>
              <p className="text-white/60 text-sm">
                Copyright <span className="text-white font-medium">Alpha Laser Technologies</span> 2026
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* SEO Hidden Content */}
      <div className="sr-only">
        <p>Alpha Laser Technologies, Alpha Laser Technologies Coimbatore, Alpha Laser - Premier laser cutting services, metal laser cutting, precision laser cutting, CNC laser services, industrial laser solutions, custom laser fabrication, laser engraving services, acrylic laser cutting, steel laser cutting near me in Coimbatore, Tamil Nadu, India.</p>
      </div>
    </footer>
  );
};

export default Footer;